import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { referralAPI, notificationsAPI } from '../api';

const ADMIN_EMAIL = 'yjc801@gmail.com';

function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const invitePanelRef = useRef(null);
  const notificationPanelRef = useRef(null);

  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [referralInvites, setReferralInvites] = useState([]);
  const [maxInvites, setMaxInvites] = useState(5);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const loadReferralInvites = async () => {
    setInvitesLoading(true);
    setInvitesError('');
    try {
      const response = await referralAPI.getMyInvites();
      setReferralInvites(response.data.invites);
      if (response.data.max_invites) {
        setMaxInvites(response.data.max_invites);
      }
    } catch (err) {
      setInvitesError('Failed to load invite links');
    } finally {
      setInvitesLoading(false);
    }
  };

  const handleToggleInvites = async () => {
    const nextState = !showInvitePanel;
    setShowInvitePanel(nextState);
    if (nextState) {
      setShowNotifications(false);
      if (referralInvites.length === 0 && !invitesLoading) {
        await loadReferralInvites();
      }
    }
  };

  const handleCopyInvite = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setInviteMessage('Link copied! Share it with a friend.');
    } catch (err) {
      setInviteMessage('Unable to copy link. Please copy it manually.');
    }

    setTimeout(() => setInviteMessage(''), 3000);
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    setNotificationsError('');
    try {
      const response = await notificationsAPI.getNotifications();
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      setNotificationsError('Failed to load notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleToggleNotifications = async () => {
    const nextState = !showNotifications;
    setShowNotifications(nextState);
    if (nextState) {
      setShowInvitePanel(false);
      if (notifications.length === 0 && !notificationsLoading) {
        await loadNotifications();
      }
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      // Update the notification in the list
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId ? { ...notification, is_read: true } : notification
        )
      );
      // Decrement unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const getNotificationMessage = (notification) => {
    const data = notification.data || {};
    switch (notification.notification_type) {
      case 'group_member_joined':
        return `${data.actor_name || 'A member'} joined ${data.group_name || 'your group'}.`;
      case 'assignments_created':
        return `Secret Santa assignments have been created for ${data.group_name || 'your group'}! Check your email for details.`;
      case 'group_settings_updated':
        return `${data.actor_name || 'The organizer'} updated the settings for ${data.group_name || 'your group'}.`;
      case 'member_removed':
        return `${data.removed_user_name || 'A member'} was removed from ${data.group_name || 'your group'}.`;
      default:
        return 'You have a new notification.';
    }
  };

  const formatTimestamp = (isoDate) => {
    if (!isoDate) return '';
    try {
      return new Date(isoDate).toLocaleString();
    } catch (err) {
      return '';
    }
  };

  const renderNotificationsContent = () => (
    <div className="referral-card">
      <div className="referral-card-header">
        <div>
          <strong>Notifications</strong>
          <div className="referral-subtext">
            Stay updated on your groups.
          </div>
        </div>
        <div className="referral-available">
          {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
        </div>
      </div>

      {notificationsError && <div className="error-message">{notificationsError}</div>}

      {notificationsLoading ? (
        <div className="loading" style={{ padding: '1rem 0' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
        </div>
      ) : notifications.length === 0 ? (
        <p className="notification-empty">No notifications yet.</p>
      ) : (
        <div className="notification-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-row ${notification.is_read ? '' : 'unread'}`}
            >
              <div>
                <div className="notification-message">
                  {getNotificationMessage(notification)}
                </div>
                <div className="notification-meta">
                  {formatTimestamp(notification.created_at)}
                </div>
              </div>
              {!notification.is_read && (
                <button
                  className="tiny-button"
                  onClick={() => handleMarkNotificationRead(notification.id)}
                >
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderInviteContent = () => (
    <div className="referral-card">
      <div className="referral-card-header">
        <div>
          <strong>Invite friends</strong>
          <div className="referral-subtext">
            Share up to {maxInvites} personal links.
          </div>
        </div>
        <div className="referral-available">
          <div className="referral-available-count">
            {invitesLoading
              ? '—'
              : `${referralInvites.filter((invite) => !invite.used).length} / ${maxInvites}`}
          </div>
          <div className="referral-available-label">available</div>
        </div>
      </div>

      {inviteMessage && (
        <div className="info-message" style={{ marginBottom: '0.75rem' }}>
          {inviteMessage}
        </div>
      )}

      {invitesError && <div className="error-message">{invitesError}</div>}

      {invitesLoading ? (
        <div className="loading" style={{ padding: '1rem 0' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
        </div>
      ) : (
        <div className="referral-list">
          {referralInvites.map((invite) => (
            <div key={invite.id} className="referral-row">
              <div className="referral-row-header">
                <button
                  type="button"
                  className="tiny-button"
                  onClick={() => handleCopyInvite(invite.invite_url)}
                  disabled={invite.used}
                >
                  Copy link
                </button>
                <span className={`status-tag ${invite.used ? 'used' : 'pending'}`}>
                  {invite.used
                    ? `Used${invite.used_at ? ` on ${new Date(invite.used_at).toLocaleDateString()}` : ''}`
                    : 'Available'}
                </span>
              </div>
              <code className="referral-url">{invite.invite_url}</code>
              {invite.used && invite.used_by && (
                <div className="referral-status">
                  <span className="referral-used-by">Claimed by {invite.used_by}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (invitePanelRef.current && !invitePanelRef.current.contains(event.target)) {
        setShowInvitePanel(false);
      }
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    // Load initial unread count when component mounts
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getNotifications();
        // Set both notifications and unread count to avoid fetching twice
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unread_count);
      } catch (err) {
        console.error('Failed to load unread count', err);
      }
    };

    fetchUnreadCount();
  }, []);

  if (!user) {
    return null;
  }

  const iconSet = {
    groups: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <circle cx="8" cy="8" r="3" fill="none"></circle>
        <circle cx="17" cy="7" r="2.5" fill="none"></circle>
        <path d="M3 19c0-2.5 2-4.5 4.5-4.5h1c2.5 0 4.5 2 4.5 4.5" fill="none"></path>
        <path d="M14 18c0-1.8 1.4-3.2 3.2-3.2H19c1.8 0 3.2 1.4 3.2 3.2" fill="none"></path>
      </svg>
    ),
    wishlist: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <rect x="4" y="9" width="16" height="11" rx="2" fill="none"></rect>
        <path d="M4 9h16" fill="none"></path>
        <path d="M9 9V6a2 2 0 014 0v3" fill="none"></path>
        <path d="M5 6h5" fill="none"></path>
        <path d="M14 6h5" fill="none"></path>
      </svg>
    ),
    notifications: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <path d="M12 21a2 2 0 001.85-1.25H10.1A2 2 0 0012 21z" fill="none"></path>
        <path d="M18 15v-4a6 6 0 10-12 0v4l-1.5 2.5h15z" fill="none"></path>
      </svg>
    ),
    invite: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <circle cx="9" cy="9" r="3" fill="none"></circle>
        <path d="M4 20c0-2.5 2-4.5 4.5-4.5H10" fill="none"></path>
        <circle cx="18" cy="8" r="2" fill="none"></circle>
        <path d="M18 12v6" fill="none"></path>
        <path d="M15 15h6" fill="none"></path>
      </svg>
    ),
    logout: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <path d="M15 3h-6a2 2 0 00-2 2v14a2 2 0 002 2h6" fill="none"></path>
        <path d="M10 12h11" fill="none"></path>
        <path d="M17 9l3 3-3 3" fill="none"></path>
      </svg>
    ),
    waitlist: (
      <svg viewBox="0 0 24 24" className="nav-icon-svg">
        <rect x="5" y="4" width="14" height="16" rx="2" fill="none"></rect>
        <path d="M8 8h8" fill="none"></path>
        <path d="M8 12h8" fill="none"></path>
        <path d="M8 16h5" fill="none"></path>
      </svg>
    )
  };

  const renderNotificationsPanel = () => (
    <div className="nav-popover-panel">
      <div className="referral-card">
        <div className="referral-card-header">
          <div>
            <strong>Notifications</strong>
            <div className="referral-subtext">
              Stay updated on your groups.
            </div>
          </div>
          <div className="referral-available">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </div>
        </div>

        {notificationsError && <div className="error-message">{notificationsError}</div>}

        {notificationsLoading ? (
          <div className="loading" style={{ padding: '1rem 0' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
          </div>
        ) : notifications.length === 0 ? (
          <p className="notification-empty">No notifications yet.</p>
        ) : (
          <div className="notification-list">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-row ${notification.is_read ? '' : 'unread'}`}
              >
                <div>
                  <div className="notification-message">
                    {getNotificationMessage(notification)}
                  </div>
                  <div className="notification-meta">
                    {formatTimestamp(notification.created_at)}
                  </div>
                </div>
                {!notification.is_read && (
                  <button
                    className="tiny-button"
                    onClick={() => handleMarkNotificationRead(notification.id)}
                  >
                    Mark read
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderInvitePanel = () => (
    <div className="nav-popover-panel">
      <div className="referral-card">
        <div className="referral-card-header">
          <div>
            <strong>Invite friends</strong>
            <div className="referral-subtext">
              Share up to {maxInvites} personal links.
            </div>
          </div>
          <div className="referral-available">
            <div className="referral-available-count">
              {invitesLoading
                ? '—'
                : `${referralInvites.filter((invite) => !invite.used).length} / ${maxInvites}`}
            </div>
            <div className="referral-available-label">available</div>
          </div>
        </div>

        {inviteMessage && (
          <div className="info-message" style={{ marginBottom: '0.75rem' }}>
            {inviteMessage}
          </div>
        )}

        {invitesError && <div className="error-message">{invitesError}</div>}

        {invitesLoading ? (
          <div className="loading" style={{ padding: '1rem 0' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
          </div>
        ) : (
          <div className="referral-list">
            {referralInvites.map((invite) => (
              <div key={invite.id} className="referral-row">
                <div className="referral-row-header">
                  <button
                    type="button"
                    className="tiny-button"
                    onClick={() => handleCopyInvite(invite.invite_url)}
                    disabled={invite.used}
                  >
                    Copy link
                  </button>
                  <span className={`status-tag ${invite.used ? 'used' : 'pending'}`}>
                    {invite.used
                      ? `Used${invite.used_at ? ` on ${new Date(invite.used_at).toLocaleDateString()}` : ''}`
                      : 'Available'}
                  </span>
                </div>
                <code className="referral-url">{invite.invite_url}</code>
                {invite.used && invite.used_by && (
                  <div className="referral-status">
                    <span className="referral-used-by">Claimed by {invite.used_by}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <nav>
      <div className="nav-content">
        <div className="nav-brand">
          <h1 onClick={() => navigate('/groups')} style={{ cursor: 'pointer' }}>
            🎅 WhoIsYourSanta
          </h1>
        </div>
        <div className="nav-links">
          <div className="nav-icon-wrapper">
            <button
              className="nav-icon-button"
              onClick={() => navigate('/groups')}
              title="Groups"
            >
              {iconSet.groups}
              <span className="nav-icon-label">Groups</span>
            </button>
          </div>
          <div className="nav-icon-wrapper">
            <button
              className="nav-icon-button"
              onClick={() => navigate('/wishlist')}
              title="Wish List"
            >
              {iconSet.wishlist}
              <span className="nav-icon-label">Wish List</span>
            </button>
          </div>
          <div className="nav-popover-wrapper nav-desktop-popover" ref={notificationPanelRef}>
            <button
              className={`nav-icon-button ${showNotifications ? 'active' : ''}`}
              onClick={handleToggleNotifications}
              title="Notifications"
            >
              {iconSet.notifications}
              {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
              <span className="nav-icon-label">Alerts</span>
            </button>
            {showNotifications && (
              <div className="notification-popover">
                {renderNotificationsContent()}
              </div>
            )}
          </div>
          <div className="nav-popover-wrapper nav-desktop-popover" ref={invitePanelRef}>
            <button
              className={`nav-icon-button ${showInvitePanel ? 'active' : ''}`}
              onClick={handleToggleInvites}
              title="Invite Friends"
            >
              {iconSet.invite}
              <span className="nav-icon-label">Invite</span>
            </button>
            {showInvitePanel && (
              <div className="referral-popover">
                {renderInviteContent()}
              </div>
            )}
          </div>
          {user.email.toLowerCase() === ADMIN_EMAIL && (
            <div className="nav-icon-wrapper">
              <button
                className="nav-icon-button"
                onClick={() => navigate('/admin/waitlist')}
                title="Waitlist Admin"
              >
                {iconSet.waitlist}
              </button>
            </div>
          )}
          <div className="nav-icon-wrapper">
            <button className="nav-icon-button" onClick={handleLogout} title="Logout">
              {iconSet.logout}
            </button>
          </div>
        </div>
        {showNotifications && (
          <div className="nav-popover-panel nav-mobile-only">
            {renderNotificationsContent()}
          </div>
        )}
        {showInvitePanel && (
          <div className="nav-popover-panel nav-mobile-only">
            {renderInviteContent()}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;
